import type * as pdfjsLib from 'pdfjs-dist';
import { PDF_PAGE_BACKGROUND } from '@/constants/pdf';
import {
  createHighlightLayer,
  createPageContainer,
  seatCanvas,
} from './pageElements';
import type { PdfViewerState } from './usePdfViewerState';

interface RenderArgs {
  pdfDoc: pdfjsLib.PDFDocumentProxy;
  pageNum: number;
  scale: number;
  state: PdfViewerState;
  /** False once a newer render (or an unmount) has superseded this one. */
  shouldContinue: () => boolean;
}

const paintToCanvas = async ({ pdfDoc, pageNum, scale, state, shouldContinue }: RenderArgs) => {
  const page = await pdfDoc.getPage(pageNum);
  if (!shouldContinue()) return null;

  const viewport = page.getViewport({ scale });

  let canvas = state.canvasRefs.current.get(pageNum);
  if (!canvas) {
    canvas = document.createElement('canvas');
    state.canvasRefs.current.set(pageNum, canvas);
  }

  const context = canvas.getContext('2d');
  if (!context) return null;

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  await page.render({ canvasContext: context, viewport }).promise;
  if (!shouldContinue()) return null;

  return { canvas, viewport };
};

/** Single-page mode: replaces the container's contents with the page. */
export async function renderSinglePage(args: RenderArgs): Promise<void> {
  const { pageNum, state } = args;
  const { containerRef, highlightLayerRefs } = state;
  if (!containerRef.current) return;

  try {
    const painted = await paintToCanvas(args);
    if (!painted) return;

    const { canvas, viewport } = painted;
    const wrapper = containerRef.current;
    if (!wrapper) return;

    let pageContainer = wrapper.querySelector(`[data-page="${pageNum}"]`) as HTMLDivElement;
    if (!pageContainer) {
      pageContainer = createPageContainer(pageNum);
      wrapper.appendChild(pageContainer);
    }

    pageContainer.innerHTML = '';
    pageContainer.style.width = `${viewport.width}px`;
    pageContainer.style.height = `${viewport.height}px`;
    seatCanvas(canvas);
    pageContainer.appendChild(canvas);

    let highlightLayer = highlightLayerRefs.current.get(pageNum);
    if (!highlightLayer) {
      highlightLayer = createHighlightLayer();
      highlightLayerRefs.current.set(pageNum, highlightLayer);
    }
    highlightLayer.innerHTML = '';
    pageContainer.appendChild(highlightLayer);
  } catch (err) {
    console.error('Error rendering page:', err);
  }
}

/** Scroll mode: paints into the placeholder container already in the flow. */
export async function renderScrollPage(args: RenderArgs): Promise<void> {
  const { pageNum, state } = args;
  const {
    containerRef, pageContainerRefs, highlightLayerRefs,
    renderedPagesRef, renderingPagesRef, setRenderedPages,
  } = state;

  if (!containerRef.current) return;

  const pageContainer = pageContainerRefs.current.get(pageNum);
  if (!pageContainer) return;

  // Checked synchronously; React state updates land too late to stop two
  // overlapping pdf.js render tasks from sharing one canvas.
  if (renderedPagesRef.current.has(pageNum) || renderingPagesRef.current.has(pageNum)) return;
  renderingPagesRef.current.add(pageNum);

  try {
    const painted = await paintToCanvas(args);
    if (!painted) return;

    const { canvas, viewport } = painted;

    pageContainer.style.width = `${viewport.width}px`;
    pageContainer.style.height = `${viewport.height}px`;
    // Drop the flex centring the placeholder used.
    pageContainer.style.display = 'block';
    pageContainer.style.alignItems = '';
    pageContainer.style.justifyContent = '';
    pageContainer.style.background = PDF_PAGE_BACKGROUND;

    const existingCanvas = pageContainer.querySelector('canvas');
    if (!existingCanvas) {
      const placeholder = pageContainer.querySelector('div:not(.highlight-layer)');
      if (placeholder) placeholder.remove();
      seatCanvas(canvas);
      pageContainer.insertBefore(canvas, pageContainer.firstChild);
    }

    let highlightLayer = highlightLayerRefs.current.get(pageNum);
    if (!highlightLayer) {
      highlightLayer = createHighlightLayer();
      highlightLayerRefs.current.set(pageNum, highlightLayer);
      pageContainer.appendChild(highlightLayer);
    }

    renderedPagesRef.current.add(pageNum);
    setRenderedPages(new Set(renderedPagesRef.current));
  } catch (err) {
    console.error('Error rendering scroll page:', err);
  } finally {
    renderingPagesRef.current.delete(pageNum);
  }
}
